import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Info, Lock } from 'lucide-react';
import { inputCls } from '../shared/FormKit';
import { FieldCommentInline } from './fieldComments';
import { FieldInvalidInline, useFieldInvalid } from '../shared/formValidation';
import {
  fieldInvalidLabel, qcTipIcon, qcTipIconActive, qcTipPopover, qcTipPanel, qcTipPanelHide, btnQcTipAll,
  linkSplitToggle, splitListTotal, splitListDate, splitListReadValue,
  splitList, splitListStackedRow, splitListTitle, splitListSubtitle,
} from './constants';
import SplitList from './SplitList';
import { formatDate } from '../../utils/dateFormat';
import { fetchJSON } from '../../utils/api';
import NowName from '../shared/NowName';
import { engagementTag } from '../../utils/engagementName';
import ScreeningTnaReference from '../shared/ScreeningTnaReference';
import {
  getQcForm, fieldsOfItem, applyAutoAnswers, lockedFieldIds, naState,
  fmtQcAnswerValue, fmtMoney, fmtItemValue, otherIdOf, CURRENCIES, EQR_NOT_REQUIRED,
  splitLines, splitIdOf, otherSplitIdOf, isChoiceSplit, choiceSplitLines,
  legacyImportItems, applyLegacyAutoAnswers, legacyLockedFieldIds,
  addendumOnlyAnswerIds, addendumOnlyHeldIds, ADDENDUM_ONLY_NOTE,
} from '../../pages/qcQuestions';

/**
 * The QC6 / QC7 acceptance form, laid out like the firm's Word forms
 * (docs/modules/workflow-qc.md):
 *
 *   header      PKF-CAP · FORM QC6(4)/QC7(4) · the client / period / type
 *               of engagement rows, read from the covered engagements
 *   block 1     Client & Engagement Particulars — a two-column table
 *   block 2     Objectives and detailed tests — the procedures table
 *               (Objective headings; each row: text, Yes/No when the row is a
 *               question, Comment)
 *   block 3     Conclusion
 *
 * `QcForm` edits, `QcFormView` reads; both render the same tables so the
 * submitter, the reviewer and Management see one form. Every question is
 * always present — the ones that do not apply are auto-filled and locked
 * (qcQuestions.applyAutoAnswers), never hidden.
 *
 * Editing surfaces: WorkflowQcNew (New), WorkflowQcDetail (Modify modal),
 * WorkflowQcaNew (QC Amendment), qcRecordKind (Management records).
 */

// ── Tip: the Word form's notes ──────────────────────────────────────────────
// Hover the Info icon → the notes float in a tooltip. Click it → they unfold
// in the cell, under the question, and stay there until clicked again (or
// "Hide"); while unfolded the hover tooltip stays away — the text is already
// on the page. `QcTipAllToggle` (in the form header) unfolds or folds every
// tip of the form at once through `QcTipProvider`; a tip toggled by hand
// afterwards keeps its own state until the next all-toggle. `defaultOpen`
// starts every tip unfolded (the QC6/7 form); without it they start folded.
const tipParagraphs = (tip) => (Array.isArray(tip) ? tip : [tip]).filter(Boolean);
// An item's tip may be a function of the current answers (the EQR row).
const tipOf = (item, answers) => (typeof item.tip === 'function' ? item.tip(answers || {}) : item.tip);

const QcTipContext = createContext({ mode: null, version: 0, setAll: null });
// `feePerEngagement`: a form covering several engagements enters every
// engagement's own Estimated fees (no Total-only) — every new form, and an
// existing one the moment its fee is changed (qcQuestions.feeAnswersChanged).
const QcFormModeContext = createContext({ feePerEngagement: false });

export const QcTipProvider = ({ children, defaultOpen = false }) => {
  const [signal, setSignal] = useState({ mode: defaultOpen ? 'open' : null, version: 0 });
  const value = useMemo(() => ({
    ...signal,
    setAll: (mode) => setSignal((s) => ({ mode, version: s.version + 1 })),
  }), [signal]);
  return <QcTipContext.Provider value={value}>{children}</QcTipContext.Provider>;
};

export const QcTipAllToggle = () => {
  const { mode, setAll } = useContext(QcTipContext);
  if (!setAll) return null;
  const allOpen = mode === 'open';
  return (
    <button type="button" onClick={() => setAll(allOpen ? 'closed' : 'open')} className={btnQcTipAll}>
      <Info className="w-3.5 h-3.5" />
      {allOpen ? 'Hide All Guidance' : 'Show All Guidance'}
    </button>
  );
};

export const QcTip = ({ tip }) => {
  const paras = tipParagraphs(tip);
  const { mode, version } = useContext(QcTipContext);
  const [expanded, setExpanded] = useState(mode === 'open');
  // The tooltip is hidden with display:none, not opacity: an invisible 26rem
  // box hanging off an icon near the right edge still widens the page and
  // grows a horizontal scrollbar. Flips to hang from the right when it would
  // spill past the viewport.
  const [hover, setHover] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const ref = useRef(null);
  // Follow the all-toggle (a tip mounted after "Show all" opens too).
  useEffect(() => { if (mode) setExpanded(mode === 'open'); }, [mode, version]);
  if (paras.length === 0) return null;
  const show = () => {
    if (expanded) return;
    const r = ref.current && ref.current.getBoundingClientRect();
    setAlignRight(!!r && r.left + 420 > window.innerWidth);
    setHover(true);
  };
  const hide = () => setHover(false);
  const toggle = () => { setHover(false); setExpanded((v) => !v); };
  return (
    <>
      <span ref={ref} className="relative inline-flex align-middle ml-1.5" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
        <button
          type="button"
          aria-label={expanded ? 'Hide guidance' : 'Show guidance'}
          aria-expanded={expanded}
          onClick={(e) => { e.preventDefault(); toggle(); }}
          className={expanded ? qcTipIconActive : qcTipIcon}
        >
          <Info className="w-3.5 h-3.5" />
        </button>
        {hover && !expanded && (
          <span role="tooltip" className={`${qcTipPopover} ${alignRight ? 'right-0' : 'left-0'}`}>
            {paras.map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
          </span>
        )}
      </span>
      {expanded && (
        <div className={qcTipPanel}>
          {paras.map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
          <button type="button" onClick={toggle} className={qcTipPanelHide}>Hide</button>
        </div>
      )}
    </>
  );
};

// ── Header: what the Word form prints above the table ───────────────────────
const HeaderRow = ({ label, children }) => (
  <tr>
    <th scope="row" className="w-48 px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 bg-slate-50 border border-slate-300 align-top">{label}</th>
    <td className="px-3 py-1.5 text-sm text-slate-900 border border-slate-300">{children}</td>
  </tr>
);

export const QcFormHeader = ({ qcType, engagements = [] }) => {
  const form = getQcForm(qcType);
  const services = [...new Set(engagements.map((e) => String(e.serviceProvided || '').trim()).filter(Boolean))];
  // 涵蓋案的 FYE 同年份、日期可不同——列出每個不同的日期，不只第一案。
  const periods = [...new Set(engagements.map((e) => (e.yearEnd ? formatDate(e.yearEnd) : '')).filter(Boolean))];
  // The client name as the engagement recorded it; "(now: …)" when the client
  // master has been renamed since (docs/data-model.md §2). Several covered
  // engagements, or one of a multi-per-FYE service, add the engagement tag
  // (engagementTag) in muted text so two engagements of one company stay apart.
  const names = engagements
    .map((e) => ({
      name: e.entityName || e.engagementName,
      now: e.entityNameNow || null,
      tag: e.entityName && (engagements.length > 1 || e.engagementSeq || e.serviceDescription)
        ? engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription) : '',
    }))
    .filter((n) => n.name)
    .map((n, i) => (
      <span key={i}>
        <NowName value={n.name} now={n.now} />
        {n.tag && <span className="text-slate-500"> · {n.tag}</span>}
      </span>
    ));
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-xs font-semibold tracking-widest text-slate-500">PKF-CAP</div>
          <h3 className="text-base font-semibold text-slate-900">{form.title}</h3>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="text-sm font-semibold text-slate-700">{form.formNo}</div>
          <QcTipAllToggle />
        </div>
      </div>
      <table className="w-full border-collapse">
        <tbody>
          <HeaderRow label={form.clientLabel}>
            {names.length === 0 ? <span className="text-slate-400">— pick the engagement(s) first</span>
              : names.length === 1 ? names[0]
                : <ul className="space-y-0.5">{names.map((n, i) => <li key={i}>{n}</li>)}</ul>}
          </HeaderRow>
          <HeaderRow label={form.periodLabel}>{periods.length > 0 ? periods.join(' / ') : <span className="text-slate-400">—</span>}</HeaderRow>
          {/* 一張 QC 可涵蓋同公司不同服務的案（審計＋AUP）——列出所有涵蓋案的服務，不只第一案。 */}
          <HeaderRow label="Type of Engagement">{services.length > 0 ? services.join(' / ') : <span className="text-slate-400">—</span>}</HeaderRow>
        </tbody>
      </table>
    </div>
  );
};

// ── Controls ────────────────────────────────────────────────────────────────
const RadioChoices = ({ name, value, onChange, options, disabled = false }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
    {options.map((o) => (
      <label key={o} className={`flex items-start gap-2 text-sm ${disabled ? 'text-slate-400' : 'text-slate-700 cursor-pointer'}`}>
        <input type="radio" name={name} className="mt-0.5" checked={value === o} disabled={disabled} onChange={() => onChange(o)} />
        <span>{o}</span>
      </label>
    ))}
  </div>
);

// The eligible engagement quality reviewers, fetched once per session
// (GET /api/workflow/qc/eqr-options = active Partners ∪ the Eligible EQR
// roster, each entry { name, position }). The answer stores the display name;
// the position only drives the designation sections of the dropdown.
let eqrOptionsPromise = null;
const fetchEqrOptions = () => {
  if (!eqrOptionsPromise) {
    eqrOptionsPromise = fetchJSON('/api/workflow/qc/eqr-options')
      .then((d) => d.reviewers || [])
      .catch(() => { eqrOptionsPromise = null; return null; });
  }
  return eqrOptionsPromise;
};

// `allowNotRequired` — the legacy-import form has no EQR-scope question to
// derive "NOT REQUIRED" from, so the dropdown offers it as a choice.
export const EqrSelect = ({ value, onChange, allowNotRequired = false }) => {
  const [reviewers, setReviewers] = useState(null); // null = loading, false = failed
  useEffect(() => {
    let on = true;
    fetchEqrOptions().then((n) => { if (on) setReviewers(n === null ? false : n); });
    return () => { on = false; };
  }, []);
  if (reviewers === false) {
    return <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder="Reviewer name" />;
  }
  const list = reviewers || [];
  // A stored name no longer on the feed (leaver, roster switch-off) stays
  // pickable at the top so the saved answer keeps rendering.
  const stale = value && value !== EQR_NOT_REQUIRED && !list.some((r) => r.name === value);
  const groupOrder = [];
  const byGroup = new Map();
  list.forEach((r) => {
    const g = r.position || 'No designation';
    if (!byGroup.has(g)) { byGroup.set(g, []); groupOrder.push(g); }
    byGroup.get(g).push(r);
  });
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} disabled={reviewers === null}>
      <option value="">{reviewers === null ? 'Loading…' : 'Select…'}</option>
      {allowNotRequired && <option value={EQR_NOT_REQUIRED}>{EQR_NOT_REQUIRED} — no engagement quality review</option>}
      {stale && <option value={value}>{value}</option>}
      {groupOrder.map((g) => (
        <optgroup key={g} label={g}>
          {byGroup.get(g).map((r) => <option key={r.name} value={r.name}>{r.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
};

// A locked (auto-filled) answer. The "Not applicable" values speak for
// themselves — no suffix.
const LockedValue = ({ value, note = null }) => (
  <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm">
    <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" title={note || 'Filled in automatically'} />
    <span className="font-medium text-slate-700">{value === undefined || value === null || String(value).trim() === '' ? '—' : String(value)}</span>
    {note && <span className="text-xs text-slate-500">{note}</span>}
  </div>
);

const MoneyInput = ({ item, answers, set, locked, amountReadOnly = false }) => {
  if (locked) return <LockedValue value={fmtMoney(item, answers)} />;
  return (
    <div className="flex gap-2">
      <select
        value={answers[`${item.id}_ccy`] || ''}
        onChange={(e) => set(`${item.id}_ccy`, e.target.value)}
        className="w-24 shrink-0 px-2 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        aria-label="Currency"
      >
        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <input
        type="number" min="0" step="0.01"
        value={answers[item.id] === undefined || answers[item.id] === null ? '' : answers[item.id]}
        onChange={(e) => set(item.id, e.target.value)}
        readOnly={amountReadOnly}
        aria-label={amountReadOnly ? `${item.label} — total of the amounts below` : undefined}
        className={`${inputCls} flex-1 min-w-0${amountReadOnly ? ' bg-slate-100 text-slate-600' : ''}`}
      />
    </div>
  );
};

// The Estimated fees of a form covering several engagements: the Total the
// requester types (required, carries the currency for every line); "Set Amount
// per Engagement" opens one amount per covered engagement — all of them
// required — under the currency picker, with their sum (applyAutoAnswers)
// as the last row. "Use Total Only" clears the amounts and frees the Total again.
const isSplitItem = (item, engagements) => !!item.split && (engagements || []).length > 1;
const splitOfAnswers = (answers, key) => (answers[key] && typeof answers[key] === 'object' && !Array.isArray(answers[key]) ? answers[key] : {});

const fmtTotal = (v) => {
  const n = Number(v);
  return v === undefined || v === null || v === '' || !Number.isFinite(n)
    ? '—'
    : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const FeeSplitInput = ({ item, answers, set, locked, engagements }) => {
  const key = splitIdOf(item);
  const split = splitOfAnswers(answers, key);
  const hasOwn = Object.keys(split).length > 0;
  const { feePerEngagement } = useContext(QcFormModeContext);
  const [open, setOpen] = useState(hasOwn || feePerEngagement);
  useEffect(() => { if (hasOwn || feePerEngagement) setOpen(true); }, [hasOwn, feePerEngagement]);
  if (locked) return <LockedValue value={fmtMoney(item, answers)} />;
  const setLine = (eid, v) => {
    const next = { ...split };
    if (v === '') delete next[eid]; else next[eid] = v;
    set(key, next);
  };
  if (!open) {
    return (
      <div>
        <MoneyInput item={item} answers={answers} set={set} locked={false} />
        <button type="button" onClick={() => { set(item.id, ''); setOpen(true); }} className={linkSplitToggle}>Set Amount per Engagement</button>
      </div>
    );
  }
  return (
    <div>
      <div className="w-24 mb-2">
        <select
          value={answers[`${item.id}_ccy`] || ''}
          onChange={(e) => set(`${item.id}_ccy`, e.target.value)}
          className={inputCls}
          aria-label="Currency"
        >
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <SplitList
        rows={splitLines(item, answers, engagements).map((l) => ({
          key: l.engagementId,
          title: l.title,
          subtitle: l.subtitle,
          control: (
            <input
              type="number" min="0" step="0.01" placeholder="0.00"
              value={l.amount === null ? '' : l.amount}
              onChange={(e) => setLine(l.engagementId, e.target.value)}
              aria-label={`Estimated fees — ${l.name}`}
              className={`${inputCls} text-right`}
            />
          ),
        }))}
        footer={(
          <>
            <span className="flex-1 text-sm font-semibold text-slate-700">Total</span>
            <span className={`shrink-0 whitespace-nowrap pr-3 ${splitListTotal}`} aria-label={`${item.label} — total`}>{answers[`${item.id}_ccy`] ? `${answers[`${item.id}_ccy`]} ` : ''}{fmtTotal(answers[item.id])}</span>
          </>
        )}
      />
      {!feePerEngagement && (
        <button type="button" onClick={() => { set(key, {}); setOpen(false); }} className={linkSplitToggle}>Use Total Only</button>
      )}
    </div>
  );
};

/**
 * The read-only text of a split fee: just the Total when no engagement has an
 * amount; else one line per engagement, then the Total.
 */
const feeSplitText = (item, answers, engagements) => {
  const lines = splitLines(item, answers, engagements);
  if (!lines.some((l) => l.amount !== null)) return fmtMoney(item, answers);
  return [...lines.map((l) => `${l.name}: ${l.text}`), `Total: ${fmtMoney(item, answers)}`].join('\n');
};

// The Expected FS Signing Date of a form covering several engagements: one
// date for every engagement, or — "Set Date per Engagement" — one date per
// engagement, all required (each starts from the shared date), and the shared
// date becomes the earliest of them (applyAutoAnswers). "Use One Date for All"
// clears them and frees the date again.
const DateSplitInput = ({ item, answers, set, locked, engagements }) => {
  const key = splitIdOf(item);
  const split = splitOfAnswers(answers, key);
  const hasOwn = Object.keys(split).length > 0;
  const [open, setOpen] = useState(hasOwn);
  useEffect(() => { if (hasOwn) setOpen(true); }, [hasOwn]);
  if (locked) return <LockedValue value={fmtItemValue(item, answers)} />;
  const setLine = (eid, v) => {
    const next = { ...split };
    if (v === '') delete next[eid]; else next[eid] = v;
    set(key, next);
  };
  const useOne = () => { set(key, {}); setOpen(false); };
  const lines = splitLines(item, answers, engagements);
  // Every line starts from the shared date — only the ones that differ need changing.
  const perEngagement = () => {
    const shared = answers[item.id];
    if (shared) set(key, Object.fromEntries(lines.map((l) => [l.engagementId, l.amount === null ? shared : l.amount])));
    setOpen(true);
  };
  if (!open) {
    return (
      <div>
        <input
          type="date" value={answers[item.id] || ''} onChange={(e) => set(item.id, e.target.value)}
          className={inputCls}
          aria-label={`${item.label} — all engagements`}
        />
        <button type="button" onClick={perEngagement} className={linkSplitToggle}>Set Date per Engagement</button>
      </div>
    );
  }
  return (
    <div>
      <SplitList
        controlClass={splitListDate}
        rows={lines.map((l) => ({
          key: l.engagementId,
          title: l.title,
          subtitle: l.subtitle,
          control: (
            <input
              type="date"
              value={l.amount === null ? '' : l.amount}
              onChange={(e) => setLine(l.engagementId, e.target.value)}
              aria-label={`${item.label} — ${l.name}`}
              className={inputCls}
            />
          ),
        }))}
      />
      <button type="button" onClick={useOne} className={linkSplitToggle}>Use One Date for All</button>
    </div>
  );
};

/**
 * The read-only text of a split date: the one date when every engagement
 * shares it, else one line per engagement with the date that applies to it.
 */
const dateSplitText = (item, answers, engagements) => {
  const all = fmtItemValue(item, answers);
  const lines = splitLines(item, answers, engagements);
  if (!lines.some((l) => l.amount !== null)) return all;
  return lines.map((l) => `${l.name}: ${l.amount !== null ? l.text : all}`).join('\n');
};
const splitText = (item, answers, engagements) => (item.type === 'date'
  ? dateSplitText(item, answers, engagements)
  : feeSplitText(item, answers, engagements));

// PIE, Type of PIE, the risk rating (and QC7's prior-year rating) and its
// reason on a form covering several engagements: one answer for all, or —
// "Set per Engagement" — one per engagement, all required, each starting from
// the shared answer (qcQuestions.splitDerivedValue says what the one answer
// then becomes). Type of PIE follows PIE: while PIE is answered per
// engagement, so is the type, and an engagement that is not a PIE shows it
// as Not applicable.
const ChoiceSplitInput = ({ item, answers, set, locked, engagements }) => {
  const key = splitIdOf(item);
  const split = splitOfAnswers(answers, key);
  const hasOwn = Object.keys(split).length > 0;
  const [open, setOpen] = useState(hasOwn);
  useEffect(() => { if (hasOwn) setOpen(true); }, [hasOwn]);
  if (locked) return <LockedValue value={fmtItemValue(item, answers)} />;
  const follows = item.splitFollows;
  const forced = !!follows && Object.keys(splitOfAnswers(answers, `${follows.id}_split`)).length > 0;
  const lines = splitLines(item, answers, engagements);
  const specKey = item.other ? otherSplitIdOf(item) : null;
  const specs = specKey ? splitOfAnswers(answers, specKey) : {};
  const setLine = (eid, v) => {
    const next = { ...split };
    if (v === '') delete next[eid]; else next[eid] = v;
    set(key, next);
  };
  const setSpec = (eid, v) => {
    const next = { ...specs };
    if (v === '') delete next[eid]; else next[eid] = v;
    set(specKey, next);
  };
  const shared = answers[item.id];
  const sharedUsable = shared !== undefined && shared !== null && String(shared).trim() !== ''
    && (item.type === 'textarea' || (item.type === 'yesno' ? ['Yes', 'No'].includes(shared) : (item.options || []).includes(shared)));
  // Every line starts from the shared answer — only the ones that differ need changing.
  const perEngagement = () => {
    if (sharedUsable) set(key, Object.fromEntries(lines.map((l) => [l.engagementId, l.amount === null ? shared : l.amount])));
    setOpen(true);
  };
  if (!open && !forced) {
    return (
      <div>
        <ItemControl item={{ ...item, split: false }} answers={answers} set={set} locked={false} />
        <button type="button" onClick={perEngagement} className={linkSplitToggle}>Set per Engagement</button>
      </div>
    );
  }
  const control = (l) => {
    const v = l.amount === null ? '' : l.amount;
    if (follows && v === follows.value) return <LockedValue value={v} />;
    const label = `${item.label} — ${l.name}`;
    switch (item.type) {
      case 'yesno':
      case 'radio':
        return <RadioChoices name={`${item.id}_${l.engagementId}`} value={v} onChange={(x) => setLine(l.engagementId, x)} options={item.type === 'yesno' ? ['Yes', 'No'] : (item.options || [])} />;
      case 'select':
        return (
          <>
            <select value={v} onChange={(e) => setLine(l.engagementId, e.target.value)} className={inputCls} aria-label={label}>
              <option value="">Select…</option>
              {(item.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {item.other && v === item.other.value && (
              <input
                type="text" value={specs[l.engagementId] || ''} onChange={(e) => setSpec(l.engagementId, e.target.value)}
                placeholder="Please specify" aria-label={`${item.other.label} — ${l.name}`} className={`${inputCls} mt-2`}
              />
            )}
          </>
        );
      default:
        return <textarea rows="2" value={v} onChange={(e) => setLine(l.engagementId, e.target.value)} aria-label={label} className={inputCls} />;
    }
  };
  return (
    <div>
      <div className={splitList}>
        {lines.map((l) => (
          <div key={l.engagementId} className={splitListStackedRow}>
            <div>
              <div className={splitListTitle}>{l.title}</div>
              {l.subtitle ? <div className={splitListSubtitle}>{l.subtitle}</div> : null}
            </div>
            {control(l)}
          </div>
        ))}
      </div>
      {!forced && (
        <button type="button" onClick={() => { set(key, {}); setOpen(false); }} className={linkSplitToggle}>Assign one answer to all entity</button>
      )}
    </div>
  );
};

const PercentInput = ({ value, onChange }) => (
  <div className="relative">
    <input type="number" min="0" step="0.1" value={value === undefined || value === null ? '' : value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} pr-9`} />
    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">%</span>
  </div>
);

/** The control for a SIMPLE (non-procedure) item. */
const ItemControl = ({ item, answers, set, locked, engagements = [] }) => {
  const v = answers[item.id];
  if (isSplitItem(item, engagements)) {
    if (isChoiceSplit(item)) return <ChoiceSplitInput item={item} answers={answers} set={set} locked={locked} engagements={engagements} />;
    return item.type === 'date'
      ? <DateSplitInput item={item} answers={answers} set={set} locked={locked} engagements={engagements} />
      : <FeeSplitInput item={item} answers={answers} set={set} locked={locked} engagements={engagements} />;
  }
  if (item.type === 'money') return <MoneyInput item={item} answers={answers} set={set} locked={locked} />;
  if (locked) return <LockedValue value={fmtItemValue(item, answers)} />;
  switch (item.type) {
    case 'yesno':
      return <RadioChoices name={item.id} value={v} onChange={(x) => set(item.id, x)} options={['Yes', 'No']} />;
    case 'radio':
      return <RadioChoices name={item.id} value={v} onChange={(x) => set(item.id, x)} options={item.options || []} />;
    case 'select':
      return (
        <>
          <select value={v || ''} onChange={(e) => set(item.id, e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {(item.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {item.other && v === item.other.value && (
            <div className="mt-2">
              <input
                type="text"
                value={answers[otherIdOf(item)] || ''}
                onChange={(e) => set(otherIdOf(item), e.target.value)}
                placeholder="Please specify"
                aria-label={item.other.label}
                className={inputCls}
              />
              <FieldInvalidInline field={`answers.${otherIdOf(item)}`} />
              <FieldCommentInline field={`answers.${otherIdOf(item)}`} />
            </div>
          )}
        </>
      );
    case 'percent':
      return <PercentInput value={v} onChange={(x) => set(item.id, x)} />;
    case 'number':
      return <input type="number" value={v === undefined || v === null ? '' : v} onChange={(e) => set(item.id, e.target.value)} className={inputCls} />;
    case 'textarea':
      return <textarea rows="2" value={v || ''} onChange={(e) => set(item.id, e.target.value)} className={inputCls} />;
    case 'eqr':
      return <EqrSelect value={v} onChange={(x) => set(item.id, x)} allowNotRequired={!!item.allowNotRequired} />;
    case 'date':
      return <input type="date" value={v || ''} onChange={(e) => set(item.id, e.target.value)} className={inputCls} />;
    default:
      return <input type="text" value={v || ''} onChange={(e) => set(item.id, e.target.value)} className={inputCls} />;
  }
};

// The small caption over a procedure sub-field (Yes / No · Comment);
// red while Submit found that sub-field empty (formValidation).
const subCaption = (invalid) => `text-[11px] font-semibold uppercase tracking-wide ${invalid ? fieldInvalidLabel : 'text-slate-500'} mb-1`;

// ── Table primitives ────────────────────────────────────────────────────────
const cellLabel = 'w-[46%] px-3 py-2 text-sm text-slate-800 bg-slate-50 border border-slate-300 align-top';
const cellValue = 'px-3 py-2 border border-slate-300 align-top';
const sectionHead = 'px-3 py-2 text-sm font-semibold text-slate-900 bg-slate-100 border border-slate-300';
const colHead = 'px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 bg-slate-100 border border-slate-300';

const BlockTitle = ({ children }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</h4>
);

const Note = ({ item }) => (
  <tr>
    <td colSpan={2} className="px-3 py-2 text-xs text-slate-600 border border-slate-300 bg-white space-y-1">
      <div className="font-semibold text-slate-700">{item.label}</div>
      {tipParagraphs(item.text).map((p, i) => <p key={i}>{p}</p>)}
    </td>
  </tr>
);

// The read-only rendering of a value, with the optional "previous answer"
// strike-through the reviewer sees after a return, and a per-field extra
// (Detail pages hang their comment anchors there).
const ReadValue = ({ field, value, previous, extra }) => {
  const now = fmtQcAnswerValue(field, value);
  const before = previous === undefined ? null : fmtQcAnswerValue(field, previous);
  const changed = before !== null && before !== now;
  return (
    <div className="group">
      <div className="text-sm text-slate-800 break-words whitespace-pre-line">
        {changed && <span className="line-through text-slate-400 mr-2">{before}</span>}
        <span className={changed ? 'font-medium' : ''}>{now}</span>
      </div>
      {extra ? extra(field) : null}
    </div>
  );
};

// A changed value shows its previous one struck through before it.
const ChangedText = ({ now, before }) => {
  const changed = before !== null && before !== undefined && before !== now;
  return (
    <>
      {changed && <span className="line-through text-slate-400 mr-2">{before}</span>}
      <span className={changed ? 'font-medium' : ''}>{now}</span>
    </>
  );
};

// The read-only view of a split item: the plain value while nothing is split;
// else the same list as the input (company, service in grey, value on the
// right), the fees closing with their Total.
const ReadSplit = ({ item, field, answers, previousAnswers, engagements, extra }) => {
  if (isChoiceSplit(item)) {
    const choice = choiceSplitLines(item, answers, engagements);
    const prevChoice = previousAnswers ? choiceSplitLines(item, previousAnswers, engagements) : null;
    if (!choice) {
      return item.other
        ? <ReadValue field={field} value={fmtItemValue(item, answers)} previous={previousAnswers ? fmtItemValue(item, previousAnswers) : undefined} extra={extra} />
        : <ReadValue field={field} value={answers[item.id]} previous={previousAnswers ? previousAnswers[item.id] : undefined} extra={extra} />;
    }
    const prevText = (i) => (prevChoice ? prevChoice[i].text : (previousAnswers ? fmtItemValue(item, previousAnswers) : null));
    return (
      <div className="group">
        <div className={splitList}>
          {choice.map((l, i) => (
            <div key={l.engagementId} className={splitListStackedRow}>
              <div>
                <div className={splitListTitle}>{l.title}</div>
                {l.subtitle ? <div className={splitListSubtitle}>{l.subtitle}</div> : null}
              </div>
              <div className="text-sm text-slate-800 break-words whitespace-pre-line"><ChangedText now={l.text} before={prevText(i)} /></div>
            </div>
          ))}
        </div>
        {extra ? extra(field) : null}
      </div>
    );
  }
  const lines = splitLines(item, answers, engagements);
  if (!lines.some((l) => l.amount !== null)) {
    return <ReadValue field={field} value={splitText(item, answers, engagements)} previous={previousAnswers ? splitText(item, previousAnswers, engagements) : undefined} extra={extra} />;
  }
  const isDate = item.type === 'date';
  const prevLines = previousAnswers ? splitLines(item, previousAnswers, engagements) : null;
  const valueOf = (l, ans) => (isDate && l.amount === null ? fmtItemValue(item, ans) : l.text);
  return (
    <div className="group">
      <SplitList
        controlClass={splitListReadValue}
        rows={lines.map((l, i) => ({
          key: l.engagementId,
          title: l.title,
          subtitle: l.subtitle,
          control: <ChangedText now={valueOf(l, answers)} before={prevLines ? valueOf(prevLines[i], previousAnswers) : null} />,
        }))}
        footer={isDate ? null : (
          <>
            <span className="flex-1 text-sm font-semibold text-slate-700">Total</span>
            <span className={splitListTotal}>
              <ChangedText now={fmtMoney(item, answers)} before={previousAnswers ? fmtMoney(item, previousAnswers) : null} />
            </span>
          </>
        )}
      />
      {extra ? extra(field) : null}
    </div>
  );
};

// An approved record's addendum-only answer, locked — per engagement when the
// form answered it that way.
const FrozenValue = ({ item, answers, engagements }) => {
  const choice = choiceSplitLines(item, answers, engagements);
  if (!choice) return <LockedValue value={fmtItemValue(item, answers)} note={ADDENDUM_ONLY_NOTE} />;
  return (
    <div className="space-y-1.5">
      {choice.map((l) => <LockedValue key={l.engagementId} value={`${l.name}: ${l.text}`} />)}
      <div className="text-xs text-slate-500">{ADDENDUM_ONLY_NOTE}</div>
    </div>
  );
};

// ── One simple item as a table row (edit or read) ───────────────────────────
const SimpleRow = ({ item, answers, set, locked, readOnly, previousAnswers, extra, addendumOnly = null, engagements = [] }) => {
  // Frozen = an approved record's addendum-only answer (rating, its reason,
  // EQR): printed locked with the note that says where it changes.
  const frozen = !readOnly && !!addendumOnly && addendumOnly.has(item.id);
  const isLocked = locked.has(item.id) || frozen;
  const field = fieldsOfItem(item)[0];
  const required = typeof item.required === 'function' ? item.required(answers) : !!item.required;
  const invalid = useFieldInvalid(`answers.${item.id}`);
  if (item.type === 'checkbox') {
    return (
      // The declaration is the one row a submitter must not overlook: tinted,
      // roomier and bolder than the answer rows above it.
      <tr>
        <td colSpan={2} className={`px-4 py-4 border border-slate-300 ${readOnly ? 'bg-white' : answers[item.id] === true ? 'bg-green-50' : 'bg-amber-50'}`}>
          {readOnly ? (
            <div className="text-sm text-slate-800">
              <span className="font-medium">{fmtQcAnswerValue(item, answers[item.id])}</span>
              <span className="text-slate-600"> — {item.label}</span>
              {extra ? extra(field) : null}
            </div>
          ) : (
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={answers[item.id] === true} onChange={(e) => set(item.id, e.target.checked)} className="mt-0.5 w-5 h-5 shrink-0" />
              <span>
                <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-0.5">Declaration{required ? ' — required' : ''}</span>
                <span className="block text-base font-semibold text-slate-900">{item.label}</span>
              </span>
            </label>
          )}
          {!readOnly && <FieldInvalidInline field={`answers.${item.id}`} text="Required — please tick the declaration" />}
          {!readOnly && <FieldCommentInline field={`answers.${item.id}`} />}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className={cellLabel}>
        <span className={invalid ? fieldInvalidLabel : undefined}>{item.label}{required && !readOnly && !isLocked ? ' *' : ''}</span>
        <QcTip tip={tipOf(item, answers)} />
      </td>
      <td className={cellValue}>
        {readOnly ? (
          isSplitItem(item, engagements)
            ? <ReadSplit item={item} field={field} answers={answers} previousAnswers={previousAnswers} engagements={engagements} extra={extra} />
            : item.type === 'money' || item.other
            ? <ReadValue field={field} value={fmtItemValue(item, answers)} previous={previousAnswers ? fmtItemValue(item, previousAnswers) : undefined} extra={extra} />
            : <ReadValue field={field} value={answers[item.id]} previous={previousAnswers ? previousAnswers[item.id] : undefined} extra={extra} />
        ) : (
          <>
            {frozen
              ? <FrozenValue item={item} answers={answers} engagements={engagements} />
              : <ItemControl item={item} answers={answers} set={set} locked={isLocked} engagements={engagements} />}
            <FieldInvalidInline field={`answers.${item.id}`} />
            <FieldCommentInline field={`answers.${item.id}`} />
          </>
        )}
      </td>
    </tr>
  );
};

// ── One procedure row: text | (Yes/No) Comment ──────────────────────────────
const ProcedureRow = ({ item, answers, set, locked, readOnly, previousAnswers, extra }) => {
  const fields = fieldsOfItem(item);
  const yn = fields.find((f) => f.id.endsWith('_yn'));
  const comment = fields.find((f) => f.id.endsWith('_comment'));
  const na = naState(item, answers);
  const commentLocked = locked.has(comment.id);
  const prev = (id) => (previousAnswers ? previousAnswers[id] : undefined);
  const ynInvalid = useFieldInvalid(yn ? `answers.${yn.id}` : null);
  const commentInvalid = useFieldInvalid(`answers.${comment.id}`);
  const no = item.no === '' || item.no === undefined ? null : String(item.no);
  const rowNa = !!na && item.naScope !== 'comment';
  return (
    <tr className={rowNa ? 'bg-slate-50/60' : ''}>
      <td className={cellLabel}>
        <div className="flex gap-2">
          {no && <span className="shrink-0 font-semibold text-slate-600">{no}</span>}
          <div className={`min-w-0 ${rowNa ? 'text-slate-500' : ''}`}>{item.label}<QcTip tip={tipOf(item, answers)} /></div>
        </div>
      </td>
      {rowNa ? (
        // The whole row does not apply — one line says so; no empty Comment
        // box to wonder about.
        <td className={cellValue}>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span>{answers[comment.id] || na.value}</span>
          </div>
          {readOnly && extra ? extra(comment) : null}
          {!readOnly && <FieldCommentInline field={`answers.${comment.id}`} />}
        </td>
      ) : (
      <td className={`${cellValue} space-y-2`}>
        {yn && (
          <div>
            <div className={subCaption(ynInvalid)}>Yes / No</div>
            {readOnly ? <ReadValue field={yn} value={answers[yn.id]} previous={prev(yn.id)} extra={extra} />
              : locked.has(yn.id) ? <LockedValue value={answers[yn.id]} />
                : <RadioChoices name={yn.id} value={answers[yn.id]} onChange={(x) => set(yn.id, x)} options={['Yes', 'No']} />}
            {!readOnly && <FieldInvalidInline field={`answers.${yn.id}`} />}
            {!readOnly && <FieldCommentInline field={`answers.${yn.id}`} />}
          </div>
        )}
        <div>
          <div className={subCaption(commentInvalid)}>Comment</div>
          {readOnly ? <ReadValue field={comment} value={answers[comment.id]} previous={prev(comment.id)} extra={extra} />
            : commentLocked ? <LockedValue value={answers[comment.id]} />
              : <textarea rows="3" value={answers[comment.id] || ''} onChange={(e) => set(comment.id, e.target.value)} className={inputCls} />}
          {!readOnly && <FieldInvalidInline field={`answers.${comment.id}`} />}
          {!readOnly && <FieldCommentInline field={`answers.${comment.id}`} />}
        </div>
      </td>
      )}
    </tr>
  );
};

// ── Blocks ──────────────────────────────────────────────────────────────────
const SectionRows = ({ section, ...rowProps }) => (
  <>
    {(section.title || section.subtitle) && (
      <tr>
        <td colSpan={2} className={sectionHead}>
          {section.title}<QcTip tip={section.tip} />
          {section.subtitle && <div className="text-xs font-normal text-slate-600 mt-0.5">{section.subtitle}</div>}
        </td>
      </tr>
    )}
    {section.items.map((item) => (
      item.type === 'note' ? <Note key={item.id} item={item} />
        : item.type === 'procedure' ? <ProcedureRow key={item.id} item={item} {...rowProps} />
          : <SimpleRow key={item.id} item={item} {...rowProps} />
    ))}
  </>
);

const Block = ({ block, isProcedures, ...rowProps }) => (
  <div className="space-y-2">
    <BlockTitle>{block.title}</BlockTitle>
    {/* No overflow wrapper: the table is fixed-width and every cell wraps, so
        a scroll container here only adds a scrollbar per block. */}
    <div>
      <table className="w-full border-collapse table-fixed">
        {isProcedures && (
          <thead>
            <tr>
              <th className={colHead}>Objectives and Detailed Tests</th>
              <th className={colHead}>Yes / No · Comment</th>
            </tr>
          </thead>
        )}
        <tbody>
          {block.sections.map((s) => <SectionRows key={s.id} section={s} {...rowProps} />)}
        </tbody>
      </table>
      {/* TNA / TNE 判斷準則：與 screening 六題同一張圖，放在表格正下方（不進格子），
          只在填答面顯示。題庫用 `reference: 'tnaTne'` 標出它跟著的區塊。 */}
      {!rowProps.readOnly && block.sections.some((s) => s.items.some((i) => i.reference === 'tnaTne')) && <ScreeningTnaReference />}
    </div>
  </div>
);

const Layout = ({ qcType, engagements, rowProps }) => {
  const form = getQcForm(qcType);
  return (
    <QcTipProvider defaultOpen>
      <div className="space-y-6">
        <QcFormHeader qcType={qcType} engagements={engagements} />
        {form.blocks.map((b) => (
          <Block key={b.id} block={b} isProcedures={b.id === 'procedures'} engagements={engagements} {...rowProps} />
        ))}
      </div>
    </QcTipProvider>
  );
};

/**
 * Editable form. `engagements` = the covered engagements (slim-list shape;
 * the first is the primary — it feeds the header). Every change runs through
 * applyAutoAnswers so the not-applicable values stay in step.
 */
/**
 * `addendumOnly` — editing an APPROVED record (QC Amendment, Management edit):
 * the Conclusion's rating, its reason and the EQR print locked with "Change
 * via QC6 / QC7 Addendum" (qcQuestions.addendumOnlyAnswerIds; the server
 * refuses a change to them with ADDENDUM_ONLY_FIELD). Pass the RECORD's
 * answers: only the answers it already holds freeze — a blank one (an
 * imported record never had the rating's reason) is still filled in here.
 * `true` freezes all three regardless.
 */
const frozenAnswerIds = (addendumOnly, qcType) => {
  if (!addendumOnly || !qcType) return null;
  if (addendumOnly === true) return new Set(addendumOnlyAnswerIds(qcType));
  // The record's held answers — same reading as the server's lock
  // (qcQuestions.addendumOnlyHeldIds: raw value, "NOT REQUIRED" counts).
  return addendumOnlyHeldIds(addendumOnly, qcType);
};

export const QcForm = ({ qcType, answers, onChange, engagements = [], addendumOnly = false, feePerEngagement = false }) => {
  const frozenIds = useMemo(() => frozenAnswerIds(addendumOnly, qcType), [addendumOnly, qcType]);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Re-sync when the bank changes (never on typing).
  useEffect(() => {
    if (!qcType) return;
    const next = applyAutoAnswers(answersRef.current || {}, qcType);
    const cur = answersRef.current || {};
    const keys = new Set([...Object.keys(next), ...Object.keys(cur)]);
    if ([...keys].some((k) => next[k] !== cur[k])) onChangeRef.current(next);
  }, [qcType]);

  const a = useMemo(() => answers || {}, [answers]);
  const locked = useMemo(() => lockedFieldIds(a, qcType), [a, qcType]);
  const set = (id, v) => onChange(applyAutoAnswers({ ...a, [id]: v }, qcType));
  if (!qcType) return null;
  return (
    <QcFormModeContext.Provider value={{ feePerEngagement }}>
      <div className="border border-slate-200 rounded-lg p-4 bg-white">
        <Layout qcType={qcType} engagements={engagements} rowProps={{ answers: a, set, locked, readOnly: false, addendumOnly: frozenIds }} />
      </div>
    </QcFormModeContext.Provider>
  );
};

/**
 * Read-only form. `previousAnswers` (optional) shows the answer before the
 * request was returned, struck through; `extra(field)` renders under every
 * value (Detail pages put their comment anchor there).
 */
export const QcFormView = ({ qcType, answers, engagements = [], previousAnswers = null, extra = null }) => {
  const a = useMemo(() => answers || {}, [answers]);
  const locked = useMemo(() => lockedFieldIds(a, qcType), [a, qcType]);
  if (!qcType) return null;
  return (
    <Layout
      qcType={qcType}
      engagements={engagements}
      rowProps={{ answers: a, set: () => {}, locked, readOnly: true, previousAnswers, extra }}
    />
  );
};

/**
 * The legacy-import form: the same header, then ONE table with only the
 * questions an imported record answers (qcQuestions.legacyImportItems) — a
 * record created from an uploaded signed form fills these and nothing else.
 * Auto-answers run over these questions only (Type of PIE locks when PIE is
 * No); the full bank's rules hang on questions this form does not ask.
 */
export const QcLegacyForm = ({ qcType, answers, onChange, engagements = [], addendumOnly = false, feePerEngagement = false }) => {
  const a = useMemo(() => answers || {}, [answers]);
  const items = useMemo(() => legacyImportItems(qcType), [qcType]);
  const locked = useMemo(() => legacyLockedFieldIds(a, qcType), [a, qcType]);
  const frozenIds = useMemo(() => frozenAnswerIds(addendumOnly, qcType), [addendumOnly, qcType]);
  const set = (id, v) => onChange(applyLegacyAutoAnswers({ ...a, [id]: v }, qcType));
  if (!qcType) return null;
  const section = { id: 'legacy_import', title: 'Answers Carried into the System', items };
  return (
    <QcFormModeContext.Provider value={{ feePerEngagement }}>
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <QcTipProvider defaultOpen>
        <div className="space-y-6">
          <QcFormHeader qcType={qcType} engagements={engagements} />
          <div className="space-y-2">
            <BlockTitle>Signed Form on File — Only These Answers Are Recorded</BlockTitle>
            <table className="w-full border-collapse table-fixed">
              <tbody>
                <SectionRows section={section} answers={a} set={set} locked={locked} readOnly={false} addendumOnly={frozenIds} engagements={engagements} />
              </tbody>
            </table>
          </div>
        </div>
      </QcTipProvider>
    </div>
    </QcFormModeContext.Provider>
  );
};

export default QcForm;
